import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Calendar, Plus, RefreshCw, Clock, AlertTriangle, CheckCircle, Bell, ChevronLeft, ChevronRight, Wrench, GraduationCap, Shield, FileText, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SafetyCalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [stats, setStats] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'safety_meeting',
    due_date: new Date().toISOString().split('T')[0],
    due_time: '',
    priority: 'medium',
    recurrence: 'none',
    notify_before_days: 7,
    location: '',
    assigned_to: []
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
    fetchEventTypes();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [eventsRes, upcomingRes, overdueRes, statsRes] = await Promise.all([
        axios.get(`${API}/safety-calendar/events`),
        axios.get(`${API}/safety-calendar/events/upcoming?days=30`),
        axios.get(`${API}/safety-calendar/events/overdue`),
        axios.get(`${API}/safety-calendar/stats`)
      ]);
      setEvents(eventsRes.data.events || []);
      setUpcoming(upcomingRes.data.events || []);
      setOverdue(overdueRes.data.events || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API}/safety-calendar/event-types`);
      setEventTypes(response.data.types || []);
    } catch (error) {
      console.error('Error fetching event types:', error);
    }
  };

  const handleCreateEvent = async () => {
    if (!formData.title || !formData.due_date) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }
    try {
      await axios.post(`${API}/safety-calendar/events`, formData);
      toast({ title: 'Success', description: 'Event created successfully!' });
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create event', variant: 'destructive' });
    }
  };

  const handleCompleteEvent = async (eventId) => {
    try {
      await axios.post(`${API}/safety-calendar/events/${eventId}/complete`);
      toast({ title: 'Success', description: 'Event marked as completed!' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to complete event', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      event_type: 'safety_meeting',
      due_date: new Date().toISOString().split('T')[0],
      due_time: '',
      priority: 'medium',
      recurrence: 'none',
      notify_before_days: 7,
      location: '',
      assigned_to: []
    });
  };

  const getEventTypeIcon = (type) => {
    const icons = {
      equipment_inspection: Wrench,
      training_expiration: GraduationCap,
      certification_renewal: FileText,
      permit_renewal: FileText,
      safety_meeting: Users,
      audit: Shield,
      drill: AlertTriangle,
      maintenance: Wrench,
      review: FileText,
      custom: Calendar
    };
    const Icon = icons[type] || Calendar;
    return <Icon className="w-4 h-4" />;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors[priority] || colors.medium;
  };

  const getDaysRemaining = (dueDate) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Calendar navigation
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const getEventsForDate = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.due_date?.startsWith(dateStr));
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
  const monthDays = [];
  for (let i = 0; i < startingDay; i++) monthDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) monthDays.push(i);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <Calendar className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Safety Calendar</h1>
              <p className="text-sm text-slate-300">Track inspections, training, permits & deadlines</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={fetchData} variant="outline" className="text-white border-white/20 hover:bg-white/10">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-blue-500 hover:bg-blue-600">
                  <Plus className="w-4 h-4 mr-2" /> Add Event
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create Safety Event</DialogTitle>
                  <DialogDescription>Schedule an inspection, training, or deadline</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="Event title" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Event Type</Label>
                      <Select value={formData.event_type} onValueChange={v => setFormData({...formData, event_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {eventTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Select value={formData.priority} onValueChange={v => setFormData({...formData, priority: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Due Date *</Label>
                      <Input type="date" value={formData.due_date} onChange={e => setFormData({...formData, due_date: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Time</Label>
                      <Input type="time" value={formData.due_time} onChange={e => setFormData({...formData, due_time: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Recurrence</Label>
                      <Select value={formData.recurrence} onValueChange={v => setFormData({...formData, recurrence: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="annual">Annual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Remind Before (days)</Label>
                      <Input type="number" value={formData.notify_before_days} onChange={e => setFormData({...formData, notify_before_days: parseInt(e.target.value) || 7})} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} placeholder="Where is this event?" />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="Additional details" rows={2} />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateEvent}>Create Event</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">This Week</p>
                  <p className="text-2xl font-bold">{stats.this_week}</p>
                </div>
                <Calendar className="w-6 h-6 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Upcoming</p>
                  <p className="text-2xl font-bold">{stats.upcoming}</p>
                </div>
                <Clock className="w-6 h-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card className={overdue.length > 0 ? 'border-red-200 bg-red-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Overdue</p>
                  <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
                </div>
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                </div>
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Completion Rate</p>
                  <p className="text-2xl font-bold">{stats.completion_rate}%</p>
                </div>
                <Bell className="w-6 h-6 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar View */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Calendar</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="font-medium min-w-[150px] text-center">
                  {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                </span>
                <Button variant="outline" size="icon" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">{day}</div>
              ))}
              {monthDays.map((day, idx) => {
                const dayEvents = day ? getEventsForDate(day) : [];
                const isToday = day && new Date().toDateString() === new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toDateString();
                return (
                  <div key={idx} className={`min-h-[80px] border rounded p-1 ${day ? 'bg-white' : 'bg-gray-50'} ${isToday ? 'ring-2 ring-blue-500' : ''}`}>
                    {day && (
                      <>
                        <p className={`text-sm ${isToday ? 'font-bold text-blue-600' : 'text-gray-700'}`}>{day}</p>
                        <div className="mt-1 space-y-1">
                          {dayEvents.slice(0, 2).map(e => (
                            <div key={e.id} className={`text-xs p-1 rounded truncate ${getPriorityColor(e.priority)}`}>
                              {e.title}
                            </div>
                          ))}
                          {dayEvents.length > 2 && (
                            <p className="text-xs text-gray-500">+{dayEvents.length - 2} more</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming & Overdue */}
        <div className="space-y-6">
          {overdue.length > 0 && (
            <Card className="border-red-200">
              <CardHeader className="bg-red-50">
                <CardTitle className="text-red-700 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> Overdue ({overdue.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                {overdue.slice(0, 5).map(event => (
                  <div key={event.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      {getEventTypeIcon(event.event_type)}
                      <div>
                        <p className="font-medium text-sm">{event.title}</p>
                        <p className="text-xs text-red-600">Due: {new Date(event.due_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleCompleteEvent(event.id)}>
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Upcoming Events</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
              {upcoming.map(event => {
                const daysLeft = getDaysRemaining(event.due_date);
                return (
                  <div key={event.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded ${getPriorityColor(event.priority)}`}>
                        {getEventTypeIcon(event.event_type)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{event.title}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(event.due_date).toLocaleDateString()} • {daysLeft > 0 ? `${daysLeft} days left` : 'Today'}
                        </p>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleCompleteEvent(event.id)}>
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
              {upcoming.length === 0 && (
                <p className="text-center text-gray-500 py-4">No upcoming events</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SafetyCalendarPage;
